<script setup>
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import MinusIcon from '@iconify-vue/ph/minus-bold';
import PlusIcon from '@iconify-vue/ph/plus-bold';
import { Link } from '@inertiajs/vue3';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { useRoute } from '@js/utils/route';
import { ref } from 'vue';

const STEP = 15;

const {
    tenant,
    objectiveName,
    minutes = 0,
    canEdit = true,
    href = null,
    callback,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    objectiveName: {
        type: String,
        required: true,
    },
    minutes: {
        type: Number,
        default: 0,
    },
    canEdit: {
        type: Boolean,
        default: true,
    },
    href: {
        type: String,
        required: false,
        default: null,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const value = ref(minutes);
const saving = ref(false);

function formatTime(total) {
    const hours = Math.floor(total / 60);
    const remainingMinutes = total % 60;

    return hours > 0
        ? `${hours}h${String(remainingMinutes).padStart(2, '0')}`
        : `${remainingMinutes}min`;
}

function update(delta) {
    const next = Math.max(0, value.value + delta);

    if (next === value.value) {
        return;
    }

    const previous = value.value;
    value.value = next;
    saving.value = true;

    axios
        .post(
            route('front.projectManagement.task.postSetObjectiveMinutes', {
                tenant: tenant.id,
            }),
            {
                objectiveName: objectiveName,
                minutes: next,
            },
        )
        .then(() => {
            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            value.value = previous;
            console.error(error);
        })
        .finally(() => {
            saving.value = false;
        });
}
</script>

<template>
    <BaseCell>
        <div class="flex items-center gap-2">
            <div v-if="canEdit" class="flex items-center gap-2">
                <button
                    type="button"
                    class="inline-flex size-6 items-center justify-center rounded-full bg-gray-200 text-gray-700 transition hover:bg-gray-300 disabled:opacity-40"
                    :disabled="saving || value <= 0"
                    @click="update(-STEP)"
                >
                    <MinusIcon class="size-3" />
                </button>

                <span
                    :class="value > 0 ? 'text-green-500' : 'text-gray-400'"
                    class="min-w-14 text-center font-bold"
                >
                    {{ formatTime(value) }}
                </span>

                <button
                    type="button"
                    class="inline-flex size-6 items-center justify-center rounded-full bg-gray-200 text-gray-700 transition hover:bg-gray-300 disabled:opacity-40"
                    :disabled="saving"
                    @click="update(STEP)"
                >
                    <PlusIcon class="size-3" />
                </button>
            </div>

            <div
                v-else
                :class="value > 0 ? 'text-green-500' : 'text-gray-400'"
                class="min-w-14 text-center font-bold"
            >
                {{ formatTime(value) }}
            </div>

            <Link
                v-if="href"
                :href="href"
                class="inline-flex size-6 items-center justify-center rounded-full text-blue-500 transition hover:bg-blue-50"
                title="Voir la feuille de route"
            >
                <ArrowSquareOutIcon class="size-4" />
            </Link>
        </div>
    </BaseCell>
</template>
